import React, { useState, useEffect } from "react";
import { apiPost } from "../../api";
import { SvgIcon } from "../SvgIcon";
import { BtnSpinner } from "../BtnSpinner";

// TODO: Replace with GET /v1/account/recovery/questions if that endpoint is added.
// Matches SECURITY_QUESTIONS in services/webmail-api/src/lib/recoveryArgon2.ts
const SECURITY_QUESTIONS: Array<{ id: number; question: string }> = [
  { id: 1,  question: "What was the name of your first pet?" },
  { id: 2,  question: "What city were you born in?" },
  { id: 3,  question: "What was the name of your first school?" },
  { id: 4,  question: "What is your mother's maiden name?" },
  { id: 5,  question: "What was the make of your first car?" },
  { id: 6,  question: "What street did you grow up on?" },
  { id: 7,  question: "What was the name of your childhood best friend?" },
  { id: 8,  question: "What is the name of the town where your parents met?" },
  { id: 9,  question: "What was the first concert you attended?" },
  { id: 10, question: "What was the name of the hospital where you were born?" },
  { id: 11, question: "What was the first album you owned?" },
  { id: 12, question: "What is your oldest sibling's middle name?" },
  { id: 13, question: "What was the name of your first employer?" },
  { id: 14, question: "What was your childhood nickname?" },
  { id: 15, question: "What was the destination of your first international trip?" },
  { id: 16, question: "What was the model of your first mobile phone?" },
  { id: 17, question: "What was the name of the street you lived on at age 10?" },
  { id: 18, question: "What is the middle name of your youngest child?" },
  { id: 19, question: "What was the name of your first stuffed animal or toy?" },
  { id: 20, question: "What was your high school mascot?" },
];

export function SecurityQuestionModal(props: {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [questionId, setQuestionId] = useState(1);
  const [answer, setAnswer] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!props.isOpen) {
      setQuestionId(1);
      setAnswer("");
      setSubmitting(false);
      setError(null);
    }
  }, [props.isOpen]);

  async function handleSubmit() {
    if (!answer.trim()) {
      setError("Please enter an answer.");
      return;
    }

    setSubmitting(true);
    setError(null);

    const res = await apiPost<{ ok: boolean }>("/v1/account/recovery/security-question", {
      questionId,
      answer: answer.trim()
    });

    setSubmitting(false);

    if (!res.ok) {
      setError(res.error || "Failed to save security question.");
      return;
    }

    props.onSuccess();
    props.onClose();
  }

  if (!props.isOpen) return null;

  return (
    <div className="modalBackdrop" role="dialog" aria-modal="true">
      <div className="modalCard" style={{ width: "min(480px, 92vw)" }}>
        <div className="modalHeader">
          <div className="modalTitle">Security question</div>
          <button className="iconBtn" onClick={props.onClose} title="Close" disabled={submitting}>
            <SvgIcon name="x-circle" title="Close" />
          </button>
        </div>

        <div className="modalBody" style={{ padding: "16px 20px" }}>
          <div style={{ fontSize: "13px", color: "var(--muted)", marginBottom: "16px", lineHeight: "1.5" }}>
            Choose a security question and provide an answer. Your answer is hashed and never stored in plain text.
          </div>

          <label style={{ display: "block", fontSize: "13px", fontWeight: 600, marginBottom: "6px" }}>
            Question
          </label>
          <select
            className="input"
            value={questionId}
            onChange={(e) => setQuestionId(Number(e.target.value))}
            disabled={submitting}
            style={{ width: "100%", boxSizing: "border-box", marginBottom: "16px" }}
          >
            {SECURITY_QUESTIONS.map((q) => (
              <option key={q.id} value={q.id}>
                {q.question}
              </option>
            ))}
          </select>

          <label style={{ display: "block", fontSize: "13px", fontWeight: 600, marginBottom: "6px" }}>
            Answer
          </label>
          <input
            type="text"
            className="input"
            value={answer}
            onChange={(e) => setAnswer(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") handleSubmit(); }}
            placeholder="Your answer"
            disabled={submitting}
            autoFocus
            style={{ width: "100%", boxSizing: "border-box" }}
          />
          <div style={{ fontSize: "11px", color: "var(--muted)", marginTop: "5px" }}>
            Answers are case-insensitive and punctuation is ignored.
          </div>

          {error && (
            <div style={{ fontSize: "12px", color: "#ef4444", marginTop: "10px" }}>
              {error}
            </div>
          )}
        </div>

        <div className="modalFooter" style={{ padding: "12px 20px" }}>
          <button className="btn" onClick={props.onClose} disabled={submitting}>
            Cancel
          </button>
          <div style={{ flex: 1 }} />
          <button
            className="btn primary"
            onClick={handleSubmit}
            disabled={submitting || !answer.trim()}
          >
            <BtnSpinner loading={submitting}>{submitting ? "Saving…" : "Save question"}</BtnSpinner>
          </button>
        </div>
      </div>
    </div>
  );
}
